import React, { useState, useMemo } from 'react';
import { Avatar, Box } from '@mui/material';
import { keyframes } from '@mui/system';

const rainbowBorderAnim = keyframes`
  0% { border-color: #ff0000; }
  17% { border-color: #ff8000; }
  33% { border-color: #ffff00; }
  50% { border-color: #00ff00; }
  67% { border-color: #0080ff; }
  83% { border-color: #8000ff; }
  100% { border-color: #ff0000; }
`;

const glowPulseAnim = keyframes`
  0%, 100% { box-shadow: 0 0 20px var(--glow-color, rgba(124,77,255,0.4)); }
  50% { box-shadow: 0 0 40px var(--glow-color, rgba(124,77,255,0.6)), 0 0 60px var(--glow-color, rgba(124,77,255,0.4)); }
`;

// Get static version URL from animated URL
function getStaticUrl(url) {
  if (!url) return null;
  const ext = url.match(/\.(gif|apng|png)$/i);
  if (!ext) return null;
  return url.replace(/\.(gif|apng|png)$/i, '-static.png');
}

export default function UserAvatar({ src, frame, size = 40, bgcolor, children, sx = {}, frameScale = 1.5, animation, glowColor, playOnHover = false, forceStatic = false, ...avatarProps }) {
  const [isHovered, setIsHovered] = useState(false);
  const frameSize = size * frameScale;
  const frameOffset = (frameSize - size) / 2;

  // Determine which avatar src to use (static vs animated)
  const staticSrc = useMemo(() => getStaticUrl(src), [src]);
  const displaySrc = useMemo(() => {
    // forceStatic: always show static (used in shop cards)
    if (forceStatic && staticSrc) return staticSrc;
    // playOnHover: show static by default, animated on hover
    if (playOnHover && staticSrc) return isHovered ? src : staticSrc;
    // Default: always show animated
    return src;
  }, [src, staticSrc, isHovered, playOnHover, forceStatic]);

  // Determine which frame src to use (static vs animated)
  const staticFrame = useMemo(() => getStaticUrl(frame), [frame]);
  const displayFrame = useMemo(() => {
    if (forceStatic && staticFrame) return staticFrame;
    if (playOnHover && staticFrame) return isHovered ? frame : staticFrame;
    return frame;
  }, [frame, staticFrame, isHovered, playOnHover, forceStatic]);

  const getAnimationStyles = () => {
    if (!animation) return {};

    const animName = typeof animation === 'string' ? animation : animation?.name;

    switch (animName) {
      case 'rainbowBorder':
        return {
          animation: `${rainbowBorderAnim} 4s linear infinite`,
          border: '3px solid #ff0000',
        };
      case 'glowPulse':
        return {
          '--glow-color': glowColor || 'rgba(124,77,255,0.4)',
          animation: `${glowPulseAnim} 2s ease-in-out infinite`,
        };
      default:
        return {};
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        // Add margin when frame is present to prevent clipping of animated frames
        m: frame ? 1.25 : 0,
      }}
      onMouseEnter={() => (playOnHover || forceStatic) && setIsHovered(true)}
      onMouseLeave={() => (playOnHover || forceStatic) && setIsHovered(false)}
    >
      <Avatar
        src={displaySrc || undefined}
        sx={{
          width: size,
          height: size,
          bgcolor,
          ...getAnimationStyles(),
          ...sx
        }}
        {...avatarProps}
      >
        {!src && children}
      </Avatar>
      {displayFrame && (
        <Box
          component="img"
          src={displayFrame}
          alt=""
          sx={{
            position: 'absolute',
            top: -frameOffset,
            left: -frameOffset,
            width: frameSize,
            height: frameSize,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
